import * as React from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"

const AccordionContext = React.createContext(null)

function Accordion({ type = "single", collapsible = false, defaultValue, className, children, ...props }) {
  const [openItems, setOpenItems] = React.useState(
    defaultValue ? (Array.isArray(defaultValue) ? defaultValue : [defaultValue]) : []
  )

  const toggleItem = React.useCallback((value) => {
    setOpenItems((prev) => {
      const isOpen = prev.includes(value)
      if (type === "single") {
        if (isOpen && collapsible) return []
        if (isOpen) return prev
        return [value]
      }
      if (isOpen) return prev.filter((v) => v !== value)
      return [...prev, value]
    })
  }, [type, collapsible])

  const contextValue = React.useMemo(() => ({ openItems, toggleItem }), [openItems, toggleItem])

  return (
    <AccordionContext.Provider value={contextValue}>
      <div className={className} {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  )
}

function AccordionItem({ value, className, children, ...props }) {
  const context = React.useContext(AccordionContext)
  const isOpen = context?.openItems.includes(value) || false

  return (
    <div 
      className={cn("border-b", className)} 
      data-state={isOpen ? "open" : "closed"}
      {...props}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { value, isOpen })
        }
        return child
      })}
    </div>
  )
}

function AccordionTrigger({ value, isOpen, className, children, ...props }) {
  const context = React.useContext(AccordionContext)

  const handleClick = () => {
    if (context && value) {
      context.toggleItem(value)
    }
  }

  return (
    <h3 className="flex">
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          "flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all hover:underline text-left",
          className
        )}
        aria-expanded={isOpen}
        {...props}
      >
        {children}
        <ChevronDown 
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
            isOpen && "rotate-180"
          )} 
        />
      </button>
    </h3>
  )
}

function AccordionContent({ isOpen, className, children, ...props }) {
  const contentRef = React.useRef(null)
  const [height, setHeight] = React.useState(0)

  React.useEffect(() => {
    if (contentRef.current) {
      setHeight(contentRef.current.scrollHeight)
    }
  }, [children])

  return (
    <div
      className="overflow-hidden transition-all duration-200 ease-in-out"
      style={{ height: isOpen ? height : 0 }}
      {...props}
    >
      <div ref={contentRef} className={cn("pb-4 pt-0", className)}>
        {children}
      </div>
    </div>
  )
}

Accordion.displayName = "Accordion"
AccordionItem.displayName = "AccordionItem"
AccordionTrigger.displayName = "AccordionTrigger"
AccordionContent.displayName = "AccordionContent"

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
